import { readPref, writePref } from '~/composables/usePersistedPref'
import { isMarketingPath } from '~/utils/marketingRoutes'
export type ColorMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'marcus_color_mode'

const preference = ref<ColorMode>('system')
const resolved = ref<'light' | 'dark'>('light')
let mediaQuery: MediaQueryList | null = null
let initialized = false

function resolveMode(pref: ColorMode): 'light' | 'dark' {
  if (pref !== 'system') return pref
  if (import.meta.server) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * The mode a path actually renders in. Public marketing pages (`isMarketingPath`) are always light,
 * whatever the user chose (decided by Alex 2026-10-17, MAR-1192; supersedes MAR-995). The stored
 * preference is never rewritten for it, so `/app` keeps the user's theme. `app/app.vue` is the one
 * place that turns this into the `dark` class on `<html>`, during SSR and after hydration.
 */
export function colorModeForPath(mode: 'light' | 'dark', path: string): 'light' | 'dark' {
  return isMarketingPath(path) ? 'light' : mode
}

function init() {
  if (initialized || import.meta.server) return
  initialized = true

  const stored = readPref(STORAGE_KEY) as ColorMode | null
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    preference.value = stored
  }

  resolved.value = resolveMode(preference.value)

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', () => {
    if (preference.value === 'system') resolved.value = resolveMode('system')
  })
}

export function useColorMode() {
  init()

  function setColorMode(mode: ColorMode) {
    preference.value = mode
    writePref(STORAGE_KEY, mode)
    resolved.value = resolveMode(mode)
  }

  return {
    preference: readonly(preference),
    resolved: readonly(resolved),
    setColorMode,
  }
}
